import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Clapperboard, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { generateCanvas, getCreative, resolveAsset } from "@/lib/afmApi";
import { SectionTitle } from "@/components/common";

const findCanvas = (data) => (data?.assets || []).find((a) => a.type === "canvas") || null;
const assetUrl = (a) => resolveAsset(a?.url || a?.video_url || a?.asset_url || a?.file_url || "");

export const CreativeCanvas = ({ releaseId }) => {
  const { user } = useAuth();
  const [asset, setAsset] = useState(null);
  const [gen, setGen] = useState(false);
  const poll = useRef(null);

  const refresh = () => getCreative(releaseId).then((d) => setAsset(findCanvas(d))).catch(() => {});

  useEffect(() => {
    refresh();
    return () => clearInterval(poll.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [releaseId]);

  useEffect(() => {
    if (asset?.status === "processing") {
      poll.current = setInterval(() => {
        getCreative(releaseId).then((d) => {
          const a = findCanvas(d);
          setAsset(a);
          if (a && a.status !== "processing") { clearInterval(poll.current); setGen(false); }
        }).catch(() => {});
      }, 3000);
      return () => clearInterval(poll.current);
    }
  }, [asset?.status, releaseId]);

  const run = async () => {
    setGen(true);
    try {
      await generateCanvas(user.id, releaseId);
      setAsset({ status: "processing" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar el Canvas");
      setGen(false);
    }
  };

  const processing = gen || asset?.status === "processing";
  const failed = asset?.status === "failed";
  const done = asset?.status === "done";
  const url = done ? assetUrl(asset) : "";

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="creative-canvas-card">
      <SectionTitle><span className="flex items-center gap-2"><Clapperboard className="h-5 w-5 text-[#AC3333]" /> Canvas visual</span></SectionTitle>

      {processing && (
        <div className="flex items-center gap-3 py-4 text-sm text-slate-500" data-testid="canvas-processing">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-[#AC3333]" /> Generando canvas visual…
        </div>
      )}

      {!processing && failed && (
        <div className="space-y-3" data-testid="canvas-error">
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
            <AlertTriangle className="h-4 w-4 shrink-0" /> {asset.error || "No se pudo generar el canvas."}
          </div>
          <button onClick={run} className="rounded-lg bg-black px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800" data-testid="canvas-retry-button">Reintentar</button>
        </div>
      )}

      {!processing && done && (
        <div className="space-y-3" data-testid="canvas-done">
          {url ? (
            <video src={url} controls loop muted className="aspect-square w-full max-w-xs rounded-xl border border-slate-200 bg-black object-cover" data-testid="canvas-video" />
          ) : (
            <p className="text-sm text-slate-500">Canvas generado correctamente.</p>
          )}
        </div>
      )}

      {!processing && !failed && !done && (
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-slate-500">Genera un video vertical en bucle (Spotify Canvas) a partir de tu portada.</p>
          <button onClick={run} className="shrink-0 rounded-lg bg-[#AC3333] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#FF0505]" data-testid="generate-canvas-button">Generar Canvas</button>
        </div>
      )}
    </div>
  );
};
